import { FC, useEffect, useState } from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Spinner,
} from "@capacit-com/lara-law-shared-ui";
import { Check } from "lucide-react";
import UserAvatar from "@/components/images/UserAvatar";
import useUserManagement from "@/hooks/useUserManagement";
import { UserWithRole } from "@/models/users/User";
import { cn } from "@/lib/utils";
import { formatUsersName } from "@/utils/format";

interface OwnerPickerProps {
  selectedUser: UserWithRole | null;
  onSelectUser: (user: UserWithRole) => void;
  disabled?: boolean;
  isActive?: boolean;
  label?: string;
}

const OwnerPicker: FC<OwnerPickerProps> = ({
  selectedUser,
  onSelectUser,
  disabled = false,
  isActive = true,
  label = "Vælg ny ejer",
}) => {
  const { useQueryUsersWithRoles } = useUserManagement();
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const { data: usersData, isLoading: isLoadingUsers } = useQueryUsersWithRoles(
    0,
    50,
    searchQuery || undefined
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchQuery(searchInput.trim());
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    if (!isActive) {
      setSearchInput("");
      setSearchQuery("");
    }
  }, [isActive]);

  const users = usersData?.data ?? [];

  return (
    <div className="space-y-3">
      <label className="text-sm font-medium block">{label}</label>

      {selectedUser && (
        <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm">
          <UserAvatar user={selectedUser} avatarClassName="size-6" />
          <div className="min-w-0">
            <p className="font-medium truncate">{formatUsersName(selectedUser)}</p>
            <p className="text-xs text-muted-foreground truncate">
              {selectedUser.email}
            </p>
          </div>
        </div>
      )}

      <Command className="border rounded-md" shouldFilter={false}>
        <CommandInput
          placeholder="Søg efter brugere..."
          value={searchInput}
          onValueChange={setSearchInput}
          disabled={disabled}
        />
        <CommandList className="max-h-[240px]">
          {isLoadingUsers ? (
            <div className="flex items-center justify-center py-8">
              <Spinner />
            </div>
          ) : (
            <>
              <CommandEmpty>Ingen brugere fundet.</CommandEmpty>
              <CommandGroup>
                {users.map((user) => (
                  <CommandItem
                    key={user.id}
                    value={`${formatUsersName(user)} ${user.email} ${user.id}`}
                    onSelect={() => onSelectUser(user)}
                    className="group"
                    disabled={disabled}
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <UserAvatar
                        user={user}
                        avatarClassName="size-6"
                        fallbackClassName="text-[10px]"
                      />
                      <div className="min-w-0">
                        <span className="block truncate">
                          {formatUsersName(user)}
                        </span>
                        <span className="block text-xs text-muted-foreground truncate">
                          {user.email}
                        </span>
                      </div>
                    </div>
                    <Check
                      className={cn(
                        "ml-auto size-4 shrink-0",
                        selectedUser?.id === user.id
                          ? "opacity-100"
                          : "opacity-0"
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}
        </CommandList>
      </Command>
    </div>
  );
};

export default OwnerPicker;
